import { motion } from "framer-motion";
import { Sun, Moon, Sparkles, Clock, Star, Flame } from "lucide-react";
import { ZodiacWheel } from "./ZodiacWheel";
import { KundliChart } from "./KundliChart";
import { BaziTable } from "./BaziTable";
import { PatternList, WesternAspectList, VedicAspectList, ChineseRelationList } from "./AspectPanel";

const ELEM_COLOR = { Fire: "#F26D6D", Earth: "#D4AF37", Air: "#7FD4A0", Water: "#45F0DF",
  Wood: "#7FD4A0", Metal: "#C9D1D9" };

function Chip({ label, value, color }) {
  return (
    <div className="rounded-lg bg-white/5 px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-sm" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function Bars({ data }) {
  const max = Math.max(1, ...Object.values(data));
  return (
    <div className="space-y-1.5">
      {Object.entries(data).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="w-16 text-[10px] text-white/50">{k}</span>
          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: ELEM_COLOR[k] || "#D4AF37" }} />
          </div>
          <span className="w-6 text-[10px] text-white/40 text-right">{v}</span>
        </div>
      ))}
    </div>
  );
}

function DignityTable({ rows, testid }) {
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-white/5">
            <td className="py-1.5 text-white/80">{r.planet}{r.retrograde ? " ℞" : ""}</td>
            <td className="py-1.5 text-white/50 text-xs">{r.sign}</td>
            <td className="py-1.5 text-right text-xs" style={{ color: /Exalt|Uccha|Domicile|Own/.test(r.dignity) ? "#7FD4A0" : /Fall|Debil|Detriment|Neecha/.test(r.dignity) ? "#F26D6D" : "rgba(255,255,255,0.55)" }}>{r.dignity}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DetailedAnalysis({ charts }) {
  const { vedic, hellenistic, chinese } = charts;
  const hd = hellenistic.detail || {};
  const vd = vedic.detail || {};
  const cd = chinese.detail || {};
  const nowYear = new Date().getFullYear();

  return (
    <div className="space-y-6" data-testid="detailed-analysis">
      {/* ---------- HELLENISTIC ---------- */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><Sun className="h-4 w-4 gold-text" strokeWidth={1.5} /><p className="overline gold-text">Hellenistic · Full Analysis</p></div>
        <div className="grid lg:grid-cols-2 gap-6">
          <ZodiacWheel planets={hellenistic.planets} ascendant={hellenistic.ascendant} accent="#D4AF37" aspects={hellenistic.aspects} angles={hellenistic.angles} showHouses showDegrees size={360} />
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <Chip label="Sect" value={hd.sect} />
              <Chip label="Chart Ruler" value={hd.chart_ruler} color="#D4AF37" />
              <Chip label="Moon Phase" value={hd.moon_phase?.name} />
            </div>
            {hd.element_balance && (
              <div className="grid grid-cols-2 gap-4">
                <div><p className="overline text-white/40 mb-2">Elements</p><Bars data={hd.element_balance} /></div>
                <div><p className="overline text-white/40 mb-2">Modality</p><Bars data={hd.modality_balance} /></div>
              </div>
            )}
            {hd.lots && (
              <div className="grid grid-cols-2 gap-2">
                <Chip label="Lot of Fortune" value={`${hd.lots.Fortune.sign} ${Math.round(hd.lots.Fortune.degree)}°`} />
                <Chip label="Lot of Spirit" value={`${hd.lots.Spirit.sign} ${Math.round(hd.lots.Spirit.degree)}°`} />
              </div>
            )}
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div>
            <p className="overline gold-text mb-2">Essential Dignities</p>
            {hd.dignities && <DignityTable rows={hd.dignities} testid="hell-dignities" />}
          </div>
          <div>
            <p className="overline gold-text mb-2">Angles</p>
            <div className="space-y-1 text-sm">
              {hellenistic.angles && Object.entries(hellenistic.angles).map(([k, g]) => (
                <div key={k} className="flex justify-between border-b border-white/5 py-1.5">
                  <span className="text-white/70">{k}</span>
                  <span className="text-white/50 text-xs">{g.sign} {Math.round(g.degree)}°</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <PatternList patterns={hellenistic.patterns} />
            <div><p className="overline gold-text mb-2">Aspects</p><WesternAspectList aspects={hellenistic.aspects} /></div>
          </div>
        </div>
      </motion.div>

      {/* ---------- VEDIC ---------- */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><Moon className="h-4 w-4 cyan-text" strokeWidth={1.5} /><p className="overline cyan-text">Vedic · Full Analysis</p></div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs text-white/50 mb-2 text-center">Rasi (D-1)</p>
            <KundliChart vedic={vedic} accent="#45F0DF" />
          </div>
          <div>
            <p className="text-xs text-white/50 mb-2 text-center">Navamsa (D-9)</p>
            {vd.navamsa && <KundliChart vedic={vd.navamsa} accent="#B47CF0" />}
          </div>
        </div>
        {vd.panchang && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
            <Chip label="Tithi" value={vd.panchang.tithi} />
            <Chip label="Vara" value={vd.panchang.vara} />
            <Chip label="Janma Nakshatra" value={`${vd.panchang.nakshatra} (${vd.panchang.nakshatra_lord})`} color="#45F0DF" />
            <Chip label="Yoga" value={vd.panchang.yoga} />
          </div>
        )}
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div>
            <p className="overline cyan-text mb-2">Dignities</p>
            {vd.dignities && <DignityTable rows={vd.dignities} testid="vedic-dignities" />}
            {vd.combustion?.length > 0 && <p className="text-xs text-white/40 mt-2"><Flame className="inline h-3 w-3" /> Combust: {vd.combustion.join(", ")}</p>}
          </div>
          <div>
            <p className="overline cyan-text mb-2">Chara Karakas</p>
            <div className="space-y-1 text-sm">
              {vd.karakas?.map((k, i) => (
                <div key={i} className="flex justify-between border-b border-white/5 py-1.5">
                  <span className="text-white/50 text-xs">{k.karaka}</span>
                  <span className="text-white/80">{k.planet}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <PatternList patterns={vedic.patterns} title="Yogas" />
            {vd.doshas?.length > 0 && (
              <div>
                <p className="overline mb-2" style={{ color: "#F26D6D" }}>Doshas</p>
                {vd.doshas.map((d, i) => (
                  <div key={i} className="rounded-lg bg-[#F26D6D]/8 border border-[#F26D6D]/20 p-2 mb-2">
                    <div className="text-sm">{d.name}</div><div className="text-xs text-white/50">{d.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {vd.dasha && (
          <div className="mt-6">
            <p className="overline cyan-text mb-1 flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Vimshottari Dasha</p>
            <p className="text-xs text-white/40 mb-3">Balance of {vd.dasha.start_lord} dasha at birth: {vd.dasha.balance_years} yrs</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {vd.dasha.sequence.map((m, i) => {
                const active = nowYear >= m.start && nowYear < m.end;
                return (
                  <div key={i} className={`flex-shrink-0 rounded-lg px-3 py-2 text-center border ${active ? "bg-[#45F0DF]/15 border-[#45F0DF]/40" : "bg-white/5 border-white/10"}`}>
                    <div className="text-sm" style={active ? { color: "#45F0DF" } : undefined}>{m.lord}</div>
                    <div className="text-[10px] text-white/40">{m.start}–{m.end}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-4"><p className="overline cyan-text mb-2">Graha Drishti</p><VedicAspectList aspects={vedic.aspects} /></div>
      </motion.div>

      {/* ---------- CHINESE ---------- */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="h-4 w-4 text-[#B47CF0]" strokeWidth={1.5} /><p className="overline" style={{ color: "#B47CF0" }}>Chinese · Full Analysis</p></div>

        {cd.pillars_detail && (
          <div className="overflow-x-auto">
            <table className="w-full text-center text-sm" data-testid="bazi-detail-table">
              <thead>
                <tr className="text-white/40 text-[10px] uppercase tracking-wider">
                  <th className="p-2 text-left">Pillar</th><th>Hour</th><th>Day</th><th>Month</th><th>Year</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/10">
                  <td className="p-2 text-left text-[10px] uppercase text-white/40">Ten God</td>
                  {["hour", "day", "month", "year"].map((k) => <td key={k} className="p-2 text-xs" style={{ color: k === "day" ? "#D4AF37" : undefined }}>{cd.pillars_detail[k].ten_god}</td>)}
                </tr>
                <tr className="border-t border-white/10">
                  <td className="p-2 text-left text-[10px] uppercase text-white/40">Heavenly Stem</td>
                  {["hour", "day", "month", "year"].map((k) => <td key={k} className="p-2"><span style={{ color: ELEM_COLOR[cd.pillars_detail[k].element] }}>{cd.pillars_detail[k].stem}</span><div className="text-[9px] text-white/40">{cd.pillars_detail[k].element}</div></td>)}
                </tr>
                <tr className="border-t border-white/10">
                  <td className="p-2 text-left text-[10px] uppercase text-white/40">Earthly Branch</td>
                  {["hour", "day", "month", "year"].map((k) => <td key={k} className="p-2"><span className="gold-text">{cd.pillars_detail[k].branch}</span><div className="text-[9px] text-white/40">{cd.pillars_detail[k].animal}</div></td>)}
                </tr>
                <tr className="border-t border-white/10">
                  <td className="p-2 text-left text-[10px] uppercase text-white/40">Hidden Stems</td>
                  {["hour", "day", "month", "year"].map((k) => <td key={k} className="p-1.5 text-[10px] text-white/60">{cd.pillars_detail[k].hidden_stems.map((h) => h.stem).join(", ")}</td>)}
                </tr>
                <tr className="border-t border-white/10">
                  <td className="p-2 text-left text-[10px] uppercase text-white/40">Na Yin</td>
                  {["hour", "day", "month", "year"].map((k) => <td key={k} className="p-2 text-[10px] text-white/50">{cd.pillars_detail[k].na_yin}</td>)}
                </tr>
                <tr className="border-t border-white/10">
                  <td className="p-2 text-left text-[10px] uppercase text-white/40">Life Stage</td>
                  {["hour", "day", "month", "year"].map((k) => <td key={k} className="p-2 text-[10px] text-white/50">{cd.pillars_detail[k].life_stage}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Chip label="Day Master" value={cd.day_master_element} color={ELEM_COLOR[cd.day_master_element]} />
              <Chip label="Strength" value={cd.day_master_strength} />
            </div>
            <div>
              <p className="overline mb-1" style={{ color: "#B47CF0" }}>Useful Gods (用神)</p>
              <div className="flex flex-wrap gap-1.5">
                {cd.useful_gods?.map((g) => <span key={g} className="rounded-full border border-white/10 px-2.5 py-1 text-xs" style={{ color: ELEM_COLOR[g] }}>{g}</span>)}
              </div>
            </div>
            {cd.element_weights && <div><p className="overline mb-2" style={{ color: "#B47CF0" }}>Element Weights</p><Bars data={cd.element_weights} /></div>}
          </div>
          <div>
            <p className="overline mb-2 flex items-center gap-1" style={{ color: "#B47CF0" }}><Clock className="h-3.5 w-3.5" /> Luck Pillars ({cd.luck_pillars?.direction})</p>
            <div className="flex flex-wrap gap-1.5">
              {cd.luck_pillars?.pillars.map((p, i) => (
                <div key={i} className="rounded-lg bg-white/5 px-2 py-1.5 text-center">
                  <div className="text-[9px] text-white/40">age {p.age_start}</div>
                  <div className="text-xs" style={{ color: ELEM_COLOR[p.element] }}>{p.stem}</div>
                  <div className="text-xs gold-text">{p.branch}</div>
                </div>
              ))}
            </div>
            {cd.symbolic_stars?.length > 0 && (
              <div className="mt-3">
                <p className="overline mb-1" style={{ color: "#B47CF0" }}>Symbolic Stars</p>
                {cd.symbolic_stars.map((s, i) => <div key={i} className="text-xs text-white/60"><Star className="inline h-3 w-3 gold-text" /> {s.name} — {s.note}</div>)}
              </div>
            )}
          </div>
          <div>
            <p className="overline mb-2" style={{ color: "#B47CF0" }}>Pillar Relations</p>
            <ChineseRelationList relations={chinese.relations} />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
